export class DomHelper {
  /**
   * Convert client coordinates to coordinates relative to a container element.
   *
   * @param ev Pointer or mouse event containing client coordinates.
   * @param container The container element to compute the relative position to.
   *
   * @returns Relative coordinates within the container.
   */
  public static getRelativePointer(ev: PointerEvent | MouseEvent, container: HTMLElement): { x: number; y: number } {
    const rect = container.getBoundingClientRect();

    return { x: ev.clientX - rect.x, y: ev.clientY - rect.y };
  }

  /**
   * Apply multiple inline styles to an element.
   * Invalid style keys are ignored.
   *
   * @param el The element to style.
   * @param styles A partial style declaration to apply.
   */
  public static setStyles(el: HTMLElement, styles: Partial<CSSStyleDeclaration>): void {
    for (const [key, value] of Object.entries(styles)) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (el.style as any)[key] = value as string;
      } catch {
        // Ignore invalid style assignment attempts
      }
    }
  }

  /**
   * Set the CSS cursor for an element.
   *
   * @param el Target element.
   * @param cursor CSS cursor value.
   */
  public static setCursor(el: HTMLElement, cursor: string): void {
    el.style.cursor = cursor;
  }

  /**
   * Make an element visible (display: '').
   *
   * @param el Target element.
   */
  public static show(el: HTMLElement): void {
    el.style.display = '';
  }

  /**
   * Hide an element (display: none).
   *
   * @param el Target element.
   */
  public static hide(el: HTMLElement): void {
    el.style.display = 'none';
  }

  /**
   * Set pointer-events mode on an element.
   *
   * @param el Target element.
   * @param mode Pointer-events value ('auto' | 'none').
   */
  public static setPointerEvents(el: HTMLElement, mode: 'auto' | 'none'): void {
    el.style.pointerEvents = mode;
  }

  /**
   * Dispatch a synthetic mouse event (click/dblclick) on an element.
   *
   * @param el Target element.
   * @param type Event type to dispatch.
   * @param init Optional event init.
   *
   * @returns True if event was not canceled by any handler.
   */
  public static dispatchMouseEvent(el: Element, type: 'click' | 'dblclick', init?: MouseEventInit): boolean {
    const evt = new MouseEvent(type, { bubbles: true, cancelable: true, view: window, ...(init || {}) });

    return el.dispatchEvent(evt);
  }

  /**
   * Safely set pointer capture on an element.
   *
   * @param element Element supporting setPointerCapture.
   * @param pointerId Pointer identifier.
   */
  public static safeSetPointerCapture(element: Element & { setPointerCapture?: (pointerId: number) => void }, pointerId: number): void {
    try {
      element.setPointerCapture?.(pointerId);
    } catch { /* Noop */ }
  }

  /**
   * Safely release pointer capture on an element.
   *
   * @param el Element supporting releasePointerCapture.
   * @param pointerId Pointer identifier.
   */
  public static safeReleasePointerCapture(el: Element & { releasePointerCapture?: (pointerId: number) => void }, pointerId: number): void {
    try {
      el.releasePointerCapture?.(pointerId);
    } catch { /* Noop */ }
  }

  /**
   * Check if a target is inside a given container.
   *
   * @param target The event target to check.
   * @param container The container element.
   *
   * @returns True if target is contained within container.
   */
  public static isTargetInside(target: EventTarget | null, container: HTMLElement | null): boolean {
    return !!(target && container && (target as Node) && container.contains(target as Node));
  }

  /**
   * Type guard to check if an html element is a button element.
   *
   * @param element Element to check.
   *
   * @returns True if element is an HTMLButtonElement.
   */
  public static isButtonElement(element?: Element | null) : element is HTMLButtonElement {
    return element?.tagName === 'BUTTON';
  }
}
